import "./rateslist.css";
import { useState, useEffect, useMemo } from "react";
import API from "src/API2";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcSearch = () => <Svg><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Svg>;
const IcClose  = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcArrow  = () => <Svg size={14}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></Svg>;
const IcEmpty  = () => <Svg size={32}><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></Svg>;

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

const statusOf = (item) => {
  const now = new Date();
  const s = new Date(item.start_date);
  const e = new Date(item.end_date);
  if (e < now) return { key: "expired", label: "Expired" };
  if (s > now) return { key: "upcoming", label: "Upcoming" };
  return { key: "active", label: "Live" };
};

const RatesList = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("ALL");

  useEffect(() => {
    setLoading(true);
    toast.promise(
      API.get("rates").then((res) => {
        const rates = res.data?.rates || [];
        return API.get("rates/offers").then((res2) => {
          const offers = res2.data?.offers || [];
          setList([...rates, ...offers]);
        });
      }),
      {
        success: "Rates loaded",
        loading: "Fetching rates…",
        error: "Could not fetch rates",
      },
    ).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return list.filter((item) => {
      const matchesSearch =
        !q ||
        item.code?.toLowerCase().includes(q) ||
        item.type?.toLowerCase().includes(q) ||
        (item.start_date || "").slice(0, 10).includes(q) ||
        (item.end_date || "").slice(0, 10).includes(q);
      const matchesType = filterType === "ALL" || item.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [list, searchQuery, filterType]);

  const counts = useMemo(() => {
    const acc = { ALL: list.length, RATE: 0, OFFER: 0 };
    list.forEach((i) => {
      if (acc[i.type] !== undefined) acc[i.type] += 1;
    });
    return acc;
  }, [list]);

  const FILTERS = [
    { key: "ALL", label: "All" },
    { key: "RATE", label: "Rates" },
    { key: "OFFER", label: "Offers" },
  ];

  return (
    <div className="rt2-list">
      {/* Toolbar */}
      <div className="rt2-toolbar">
        <div className="rt2-search">
          <IcSearch />
          <input
            type="text"
            placeholder="Search by code, type or date…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="rt2-search__clear" onClick={() => setSearchQuery("")} aria-label="Clear">
              <IcClose />
            </button>
          )}
        </div>
        <div className="rt2-filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`rt2-pill ${filterType === f.key ? "is-active" : ""}`}
              onClick={() => setFilterType(f.key)}
            >
              {f.label}
              <span className="rt2-pill__count">{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Card / table */}
      <div className="rt2-card">
        <div className="rt2-table-wrap">
          <table className="rt2-table">
            <thead>
              <tr>
                <th className="rt2-no">#</th>
                <th>Code</th>
                <th>Type</th>
                <th>Validity</th>
                <th>Status</th>
                <th className="rt2-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="rt2-loading">Loading rates…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="6">
                    <div className="rt2-empty">
                      <IcEmpty />
                      <h3>{list.length ? "No matches" : "No rates yet"}</h3>
                      <p>
                        {list.length
                          ? `Nothing matches your filters.`
                          : "Create your first rate or promotional offer."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((e, i) => {
                  const st = statusOf(e);
                  const typeKey = (e.type || "RATE").toLowerCase();
                  return (
                    <tr
                      key={e.id || i}
                      className="rt2-row"
                      onClick={() => { window.location.href += `/edit/${e.id}`; }}
                    >
                      <td className="rt2-no">{i + 1}</td>
                      <td>
                        <span className="rt2-code-chip">{e.code}</span>
                      </td>
                      <td>
                        <span className={`rt2-type rt2-type--${typeKey}`}>{e.type}</span>
                      </td>
                      <td>
                        <div className="rt2-stack">
                          <span className="rt2-line">{fmtDate(e.start_date)} → {fmtDate(e.end_date)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`rt2-status rt2-status--${st.key}`}>{st.label}</span>
                      </td>
                      <td className="rt2-actions">
                        <button
                          className="rt2-icon-btn"
                          onClick={(ev) => { ev.stopPropagation(); window.location.href += `/edit/${e.id}`; }}
                          title="Edit"
                        >
                          <IcArrow />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <p className="rt2-table__footnote">
            Showing {filtered.length} of {list.length} · Click a row to edit.
          </p>
        )}
      </div>
    </div>
  );
};

export default RatesList;
